import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  App,
  Button,
  Checkbox,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { vendorApi } from '@/utils/vendorApi';
import { businessUnitLabel, useAuthStore } from '@/store/authStore';
import { canUpdateVendor } from '@/store/vendorAccess';
import { ALL_BUSINESS_UNITS_ID, matchesSelectedBu } from '@/utils/businessUnits';
import { tagFilterParam, tagSelectOptions, useCatalogTags } from '@/utils/tagOptions';
import { ALL_VENDORS, useVendorIdOptions } from '@/utils/vendorIdOptions';
import { OnboardingStatus, VendorItem, VendorListParams } from '@/types';

const { Text } = Typography;

type StatusFilter = OnboardingStatus | 'all';

function toListParams(
  vendorId: string,
  status: StatusFilter,
  includeArchived: boolean,
  tag: string,
  nextToken?: string,
): VendorListParams {
  return {
    vendorId: vendorId.trim() || undefined,
    status: status === 'all' ? undefined : status,
    includeArchived: includeArchived || undefined,
    tag: tagFilterParam(tag),
    nextToken,
  };
}

function formatUpdatedAt(value?: string): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return '—';
  }
  return parsed.format('MMM DD, YYYY HH:mm');
}

const OPERATOR_STATUS_VALUES: StatusFilter[] = ['all', 'pending', 'completed'];

function initialOperatorStatus(): StatusFilter {
  const value = new URLSearchParams(window.location.search).get('status');
  return OPERATOR_STATUS_VALUES.includes(value as StatusFilter) ? (value as StatusFilter) : 'all';
}

const OperatorVendorList: React.FC = () => {
  const { message } = App.useApp();
  const { authorization, currentBusinessUnit } = useAuthStore();
  const tagChoices = useCatalogTags();
  const navigate = useNavigate();

  const openVendor = (record: VendorItem) => {
    const hostname = record.hostname || 'default';
    navigate(
      `/my-vendors/${encodeURIComponent(record.vendorId)}?hostname=${encodeURIComponent(hostname)}`
    );
  };

  const [vendorId, setVendorId] = useState(ALL_VENDORS);
  const [status, setStatus] = useState<StatusFilter>(initialOperatorStatus);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [tag, setTag] = useState('all');
  const [vendors, setVendors] = useState<VendorItem[]>([]);
  const { vendorIdOptions, optionsLoading, optionRows } = useVendorIdOptions(
    'operator',
    includeArchived,
  );
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(
    async (
      vendorFilter: string,
      statusFilter: StatusFilter,
      includeArchivedFilter: boolean,
      tagFilter: string,
      append: boolean,
      cursor?: string,
    ) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        const response = await vendorApi.listOperatorVendorsPage(
          toListParams(vendorFilter, statusFilter, includeArchivedFilter, tagFilter, cursor)
        );
        setVendors((prev) => (append ? [...prev, ...response.vendors] : response.vendors));
        setNextToken(response.nextToken);
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load vendors';
        message.error(errorMessage);
        if (!append) {
          setVendors([]);
          setNextToken(undefined);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [message]
  );

  useEffect(() => {
    void fetchPage(vendorId, status, includeArchived, tag, false);
  }, [vendorId, status, includeArchived, tag, fetchPage]);

  const handleStatusChange = (nextStatus: StatusFilter) => {
    setVendors([]);
    setNextToken(undefined);
    setStatus(nextStatus);
  };

  const handleIncludeArchivedChange = (checked: boolean) => {
    setVendors([]);
    setNextToken(undefined);
    setIncludeArchived(checked);
  };

  const selectedId = currentBusinessUnit?.id;

  useEffect(() => {
    if (optionRows === null || !vendorId) {
      return;
    }
    const stillListed = vendorIdOptions.some((option) => option.value === vendorId);
    if (!stillListed) {
      setVendorId(ALL_VENDORS);
    }
  }, [optionRows, vendorIdOptions, vendorId]);

  const visibleVendors = selectedId
    ? vendors.filter((row) => matchesSelectedBu(row.bu, selectedId, authorization?.catalog))
    : vendors;
  const filterNote = selectedId && selectedId !== ALL_BUSINESS_UNITS_ID && nextToken
    ? `Filtered to ${businessUnitLabel(selectedId)}. Load more to see additional results.`
    : null;

  const columns: ColumnsType<VendorItem> = [
    { title: 'Vendor ID', dataIndex: 'vendorId', key: 'vendorId', ellipsis: true },
    {
      title: 'BU',
      dataIndex: 'bu',
      key: 'bu',
      width: 140,
      render: (bu: string | null) => bu || '—',
    },
    {
      title: 'Tag',
      dataIndex: 'tag',
      key: 'tag',
      width: 140,
      render: (value: string | null | undefined) => value || '—',
    },
    { title: 'SFTP username', dataIndex: 'sftpUsername', key: 'sftpUsername', ellipsis: true },
    {
      title: 'Onboarding',
      dataIndex: 'onboardingStatus',
      key: 'onboardingStatus',
      width: 130,
      render: (onboardingStatus: string) => {
        const normalized = (onboardingStatus || '').toLowerCase();
        const color = normalized === 'completed' ? 'green' : normalized === 'pending' ? 'gold' : 'default';
        return <Tag color={color}>{onboardingStatus || '—'}</Tag>;
      },
    },
    {
      title: 'Lifecycle',
      dataIndex: 'lifecycleStatus',
      key: 'lifecycleStatus',
      width: 120,
      render: (lifecycleStatus: string | undefined) => (
        lifecycleStatus === 'archived' ? <Tag color="purple">Archived</Tag> : null
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
      render: (value: string | undefined) => formatUpdatedAt(value),
    },
    {
      title: '',
      key: 'edit',
      width: 130,
      render: (_value: unknown, record: VendorItem) => (
        canUpdateVendor(authorization, record.bu) ? (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              openVendor(record);
            }}
          >
            Edit Vendor
          </Button>
        ) : null
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Filter by Vendor ID and onboarding status
        </Text>
        <Select
          style={{ width: 220, marginRight: 16 }}
          showSearch
          loading={optionsLoading}
          value={vendorId}
          onChange={(next: string) => {
            setVendors([]);
            setNextToken(undefined);
            setVendorId(next);
          }}
          options={vendorIdOptions}
          optionFilterProp="label"
        />
        <Select
          style={{ width: 200, marginRight: 16 }}
          value={tag}
          onChange={(next: string) => {
            setVendors([]);
            setNextToken(undefined);
            setTag(next);
          }}
          options={tagSelectOptions(tagChoices)}
        />
        <Select
          style={{ width: 200 }}
          value={status}
          onChange={handleStatusChange}
          options={[
            { value: 'all', label: 'All' },
            { value: 'pending', label: 'pending' },
            { value: 'completed', label: 'completed' },
          ]}
        />
        <Checkbox
          style={{ marginLeft: 16 }}
          checked={includeArchived}
          onChange={(event) => handleIncludeArchivedChange(event.target.checked)}
        >
          Include archived
        </Checkbox>
      </div>

      <Table<VendorItem>
        columns={columns}
        dataSource={visibleVendors}
        rowKey={(row) => `${row.vendorId}::${row.hostname || 'default'}`}
        loading={loading}
        pagination={false}
        size="small"
        onRow={(record) => ({
          onClick: () => openVendor(record),
          style: { cursor: 'pointer' },
        })}
      />

      {nextToken && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          {filterNote && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>{filterNote}</Text>
          )}
          <Button onClick={() => void fetchPage(vendorId, status, includeArchived, tag, true, nextToken)} loading={loadingMore}>
            Load more
          </Button>
        </div>
      )}
    </>
  );
};

export default OperatorVendorList;
